'use client';

import { useCallback } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Menu, Text } from '@terra/ui-design-system';
import { Portal } from '@ark-ui/react';
import {
  Cancel01Icon,
  PinIcon,
  FolderAddIcon,
  FolderMinusIcon,
  LinkSquare02Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { type Tab, type TabGroup } from '../../../store/layoutStore';
import { getGroupPickerFillColor } from './tabGroupDecorativeColor';

type TabContextMenuProps = {
  open: boolean;
  anchorRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  contextMenuTabData: Tab | null;
  tabGroups: TabGroup[];
  selectedCount: number;
  shouldUnpin: boolean;
  canPinTabs: boolean;
  canDetachTabs: boolean;
  canRemoveFromGroup: boolean;
  onClose: () => void;
  onPinTabs: () => void;
  onDetachTab: () => void;
  onCreateGroup: () => void;
  onAddToExistingGroup: (groupId: string) => void;
  onRemoveFromGroup: () => void;
  onCloseTabs: () => void;
};

export function TabContextMenu({
  open,
  anchorRect,
  contextMenuTabData,
  tabGroups,
  selectedCount,
  shouldUnpin,
  canPinTabs,
  canDetachTabs,
  canRemoveFromGroup,
  onClose,
  onPinTabs,
  onDetachTab,
  onCreateGroup,
  onAddToExistingGroup,
  onRemoveFromGroup,
  onCloseTabs,
}: TabContextMenuProps) {
  const t = useTranslations('layout');
  const tDetached = useTranslations('proxy.detachedWindows');
  const pinLabel = shouldUnpin ? t('unpin') : t('pin');
  const closeLabel = selectedCount > 1 ? t('closeSelectedTabs') : t('closeTab');
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Menu.Root
      open={open}
      onOpenChange={({ open: nextOpen }) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
      positioning={{
        strategy: 'fixed',
        placement: 'bottom-start',
        gutter: 4,
        flip: false,
        ...(anchorRect && {
          getAnchorRect: () => anchorRect,
        }),
      }}
      onSelect={(details) => {
        if (details.value.startsWith('add-to-group-')) {
          const groupId = details.value.slice('add-to-group-'.length);

          onAddToExistingGroup(groupId);
          handleClose();

          return;
        }

        switch (details.value) {
          case 'pin-tab':
            onPinTabs();
            break;
          case 'detach-tab':
            onDetachTab();
            break;
          case 'create-group':
            onCreateGroup();
            break;
          case 'remove-from-group':
            onRemoveFromGroup();
            break;
          case 'close-tabs':
            onCloseTabs();
            break;
          default:
            return;
        }

        handleClose();
      }}
    >
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            minW="180px"
            py="1"
            borderWidth="1px"
            borderColor="border.subtle"
          >
            {canPinTabs && (
              <Menu.Item value="pin-tab" fontSize="xs" py="1" px="2">
                <HStack gap="1.5">
                  <PinIcon size={12} strokeWidth={1.75} />
                  <Text fontSize="xs">{pinLabel}</Text>
                </HStack>
              </Menu.Item>
            )}
            {canDetachTabs && contextMenuTabData && (
              <Menu.Item value="detach-tab" fontSize="xs" py="1" px="2">
                <HStack gap="1.5">
                  <LinkSquare02Icon size={12} strokeWidth={1.75} />
                  <Text fontSize="xs">{tDetached('moveToNewWindow')}</Text>
                </HStack>
              </Menu.Item>
            )}
            <Menu.Item value="create-group" fontSize="xs" py="1" px="2">
              <HStack gap="1.5">
                <FolderAddIcon size={12} strokeWidth={1.75} />
                <Text fontSize="xs">{t('createGroupTab')}</Text>
              </HStack>
            </Menu.Item>
            {tabGroups.length > 0 && (
              <Menu.ItemGroup>
                <Menu.Separator />
                <Menu.ItemGroupLabel py="0.5" px="2">
                  <Text fontSize="2xs" color="fg.muted">
                    {t('addToExistingGroup')}
                  </Text>
                </Menu.ItemGroupLabel>
                {tabGroups.map((group) => (
                  <Menu.Item
                    key={group.id}
                    value={`add-to-group-${group.id}`}
                    fontSize="xs"
                    py="1"
                    px="2"
                  >
                    <HStack gap="1.5">
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="xs"
                        style={{
                          backgroundColor: getGroupPickerFillColor(group.color),
                        }}
                      />
                      <Text fontSize="xs">{group.name}</Text>
                    </HStack>
                  </Menu.Item>
                ))}
              </Menu.ItemGroup>
            )}
            {canRemoveFromGroup && (
              <Menu.Item value="remove-from-group" fontSize="xs" py="1" px="2">
                <HStack gap="1.5">
                  <FolderMinusIcon size={12} strokeWidth={1.75} />
                  <Text fontSize="xs">{t('removeFromGroup')}</Text>
                </HStack>
              </Menu.Item>
            )}
            <Menu.Item value="close-tabs" fontSize="xs" py="1" px="2">
              <HStack gap="1.5">
                <Cancel01Icon size={12} strokeWidth={1.75} />
                <Text fontSize="xs">{closeLabel}</Text>
              </HStack>
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
